import React, { useState, useEffect, useCallback } from 'react';

import { InputLabel } from '@material-ui/core';
import PostField from 'components/feed/PostField';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, shallowEqual } from 'react-redux';
import Select from 'react-select';
import CollaboratorField from 'screens/common/sidepanel/DetailSection/CollaboratorField';
import DueDateField from 'screens/common/sidepanel/DetailSection/DueDateField';
import GoalObjectiveField from 'screens/common/sidepanel/DetailSection/GoalObjectiveField';
import TrackingField from 'screens/common/sidepanel/DetailSection/GoalTracking/TrackingField';
import ObjectiveField from 'screens/common/sidepanel/DetailSection/ObjectiveField';
import OwnerField from 'screens/common/sidepanel/DetailSection/OwnerField';
import { SingleValueOption, VisibilityOption } from 'screens/common/sidepanel/DetailSection/VisibilityField';
import {
  IndianNumberFormatCustom,
  ForeignNumberFormatCustom,
} from 'screens/common/sidepanel/Goals/GoalHelperComponents';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import {
  EditableTextFieldCSS,
  StyledDivider,
  StyledGrid,
  StyledTextField,
  StyledTypography,
  VerticallyAlignedFlexContainer,
  hideNumberInputArrowsCSS,
} from 'styles/common';

import { GOAL_VISIBILITY_OPTIONS } from 'constants/ProjectConstants';
import { capitalize, getOrgGoalMeasurementTypes } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import theme from 'constants/theme';

import { Typography17 } from 'uiToolkit/Typography';

import CreateGoalDescriptionField from './CreateGoalDescriptionField';
import { updateGoalProperty, updateGoal } from './newGoalActions';

// need to move common functions in one file. using below one with export
export function Heading({ children, ...props }) {
  return (
    <StyledTypography variant="body2" weight={600} color="textSecondary" {...props}>
      {children}
    </StyledTypography>
  );
}

function MeasureComponent({ goalDetails, updateGoal }) {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const orgGoalMeasurementTypes = getOrgGoalMeasurementTypes(loggedInEmployee?.organization?.currency ?? null);
  const optionsForSelect = orgGoalMeasurementTypes.map(({ symbol, code }) => ({
    label: `${capitalize(code)} (${symbol})`,
    value: code,
  }));

  return (
    <MeasuredWithSelect
      options={optionsForSelect}
      value={optionsForSelect.filter(({ value }) => value === goalDetails.measure)[0]}
      onChange={({ value }) => {
        updateGoal({ ...goalDetails, measure: value }, false);
      }}
      classNamePrefix="measured-with"
    />
  );
}

export const VisibilityComponent = ({
  goalDetails,
  updateGoal,
  defaultGoalVisibility,
  isCreateGoalFormOld = false,
}) => {
  useEffect(() => {
    if (defaultGoalVisibility) {
      updateGoal({ ...goalDetails, visibilityType: defaultGoalVisibility }, false);
    }
  }, [defaultGoalVisibility]);

  return (
    <VisibilitySelect
      options={GOAL_VISIBILITY_OPTIONS}
      defaultValue={GOAL_VISIBILITY_OPTIONS.find(({ value }) => value === defaultGoalVisibility)}
      value={GOAL_VISIBILITY_OPTIONS.filter(({ value }) => value === goalDetails.visibilityType)[0]}
      onChange={({ value }) => {
        updateGoal({ ...goalDetails, visibilityType: value }, false);
      }}
      classNamePrefix="measured-with"
      components={{ Option: VisibilityOption, SingleValue: SingleValueOption }}
      isCreateGoalFormOld={isCreateGoalFormOld}
    />
  );
};

const Container = styled.div``;

const InputPaddingCSS = css`
  padding: 0.5rem 0.75rem;
`;

const StartingField = styled(StyledTextField)`
  .MuiInputBase-root {
    font-size: ${(props) => props.theme.typography.body2.fontSize};
    height: 2.5rem;
  }
  ${EditableTextFieldCSS};
  input {
    ${hideNumberInputArrowsCSS};
    ${InputPaddingCSS};
  }
  fieldset {
    border-width: 1px;
  }
  margin-top: 4px;
  margin-bottom: 4px;
`;

const DueDateWrapper = styled.div``;

const editorStyles = css`
  border-radius: 4px;
  line-height: 2rem;
  font-size: 1.2rem;
  font-weight: 500;
`;

const editorPlaceholderStyles = css`
  font-size: ${isMobileView() ? '1rem' : '1.2rem'};
  line-height: 2rem;
  font-weight: 500;
  padding: 1rem 0.75rem;
  margin: 0;
  color: ${theme.palette.text.placeholder};
`;

const MeasuredWithSelect = styled(Select)`
  .measured-with__control {
    height: 2.5rem;
  }
`;

const VisibilitySelect = styled(Select)`
  .measured-with__control {
    height: 2.5rem;
  }
`;

const SelectWrapper = styled.div`
  display: flex;
  margin-left: 32px;
  & div {
  }
`;

export default function CreateGoalForm({
  goalDetails: taskDetails,
  toggleShowTrackingDetails,
  dispatch,
  teamId,
  goalDueDateTS,
  goalStartDateTS,
  setGoalDueDateTS = () => {},
  setGoalStartDateTS = () => {},
  defaultGoalVisibility = GOAL_VISIBILITY_OPTIONS[1]?.value,
  parentPageSelectedTimeBoundPeriod = null,
}) {
  const updateTask = (updatedTask) => dispatch(updateGoal(updatedTask));

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const employeeObj = useSelector(selectLoggedInEmployeeData);
  let orgCurrency = employeeObj?.organization?.currency ?? null;
  if (!orgCurrency) {
    orgCurrency = 'INR';
  }
  const [editorState, setEditorState] = useState(taskDetails.editorState);
  const [description, setDescription] = useState(taskDetails.description);

  useEffect(() => {
    setEditorState(taskDetails.editorState);
  }, [taskDetails.editorState]);

  useEffect(() => {
    setDescription(taskDetails.description);
  }, [taskDetails.description]);

  const coloredDueDate = taskDetails.coloredDueDate || {};
  const coloredStartDate = taskDetails.coloredStartDate || {};

  function onKeyDown(e) {
    if (e.keyCode === 13 || e.keyCode === 27) {
      e.stopPropagation();
      e.preventDefault();
      e.target.blur();
    }
  }

  function renderObjectiveSection() {
    return (
      <StyledGrid bottomGutter={2} container>
        <StyledGrid alignSelf="center" item xs={3}>
          <Heading topGutter={1}>Objective*</Heading>
        </StyledGrid>
        <StyledGrid item xs={9}>
          <ObjectiveField taskDetails={taskDetails} updateTask={updateTask} />
        </StyledGrid>
      </StyledGrid>
    );
  }

  function renderTrackingSection() {
    return (
      <StyledGrid bottomGutter={2} container>
        <StyledGrid alignSelf="center" item xs={3}>
          <Heading topGutter={1}>Tracking</Heading>
        </StyledGrid>
        <StyledGrid item xs={9}>
          <TrackingField taskDetails={taskDetails} toggleShowTrackingDetails={toggleShowTrackingDetails} />
        </StyledGrid>
      </StyledGrid>
    );
  }

  const handleUpdate = useCallback((value) => dispatch(updateGoalProperty('description', value)), [dispatch]);

  return (
    <Container>
      <div style={{ marginBottom: '1.5rem' }}>
        <VerticallyAlignedFlexContainer>
          <InputLabel>
            <Typography17 weight={600}>{`${objectivesName}*`}</Typography17>
          </InputLabel>

          {/* <TooltipInfo
            title={KH_LINKS.CREATE_GOAL.description}
            linkUrl={KH_LINKS.CREATE_GOAL.url}
            leftGutter={2.25}
          /> */}
        </VerticallyAlignedFlexContainer>
        <div style={{ marginTop: '1rem' }}>
          <GoalObjectiveField
            taskDetails={taskDetails}
            updateTask={updateTask}
            parentPageSelectedTimeBoundPeriod={parentPageSelectedTimeBoundPeriod}
          />
        </div>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <Typography17 bottomGutter="0.5rem" weight={600}>
          {`${keyResultsName}*`}
        </Typography17>
        <PostField
          placeholder="e.g. Hire 10 manager roles in the next quarter"
          editorState={editorState}
          updateEditorState={setEditorState}
          handleBlur={() => dispatch(updateGoalProperty('editorState', editorState))}
          editorStyles={editorStyles}
          useMentionFunctionality
          placeholderStyles={editorPlaceholderStyles}
          onEnter={() => {
            dispatch(updateGoalProperty('editorState', editorState));
          }}
          onEscape={() => {
            dispatch(updateGoalProperty('editorState', editorState));
          }}
          teamId={teamId} // this is the right fix, you will be able to create hashtag like this, not like in the above case
          useHashtagFunctionality={false}
        />
      </div>

      <StyledGrid bottomGutter={2} container spacing={10}>
        <StyledGrid style={{ paddingRight: '1rem', marginTop: '1rem' }} item xs={6}>
          <InputLabel>
            <Typography17 bottomGutter={0.5} weight={600}>
              Starting Value*
            </Typography17>
          </InputLabel>
          <StartingField
            name="startingProgressValue"
            value={taskDetails.startingProgressValue}
            onChange={(e) => {
              dispatch(updateGoalProperty('startingProgressValue', e.target.value));
              dispatch(updateGoalProperty('actual', e.target.value));
            }}
            variant="outlined"
            size="small"
            InputProps={{
              inputComponent: orgCurrency === 'INR' ? IndianNumberFormatCustom : ForeignNumberFormatCustom,
            }}
            fullWidth
            placeholder="Enter starting value"
          />
        </StyledGrid>
        <StyledGrid style={{ paddingLeft: '1rem', marginTop: '1rem' }} item xs={6}>
          <InputLabel>
            <Typography17 bottomGutter={0.5} weight={600}>
              Target Value*
            </Typography17>
          </InputLabel>
          <StartingField
            name="target"
            value={taskDetails.target ?? ''}
            onChange={(e) => dispatch(updateGoalProperty('target', e.target.value))}
            variant="outlined"
            size="small"
            InputProps={{
              inputComponent: orgCurrency === 'INR' ? IndianNumberFormatCustom : ForeignNumberFormatCustom,
            }}
            fullWidth
            placeholder="Value to achieve"
          />
        </StyledGrid>
        <StyledGrid style={{ paddingRight: '1rem', marginTop: '1rem' }} item xs={6}>
          <InputLabel>
            <Typography17 bottomGutter={0.5} weight={600}>
              Metric Name
            </Typography17>
          </InputLabel>
          <StartingField
            name="measureType"
            value={taskDetails.measureType ?? ''}
            onChange={(e) => {
              dispatch(updateGoalProperty('measureType', e.target.value));
              dispatch(updateGoalProperty('measureType', e.target.value));
            }}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="e.g. Revenue, NPS etc."
          />
        </StyledGrid>

        <StyledGrid style={{ paddingLeft: '1rem', marginTop: '1rem' }} item xs={6}>
          <InputLabel style={{ marginBottom: '0.5rem' }}>
            <Typography17 bottomGutter={0.5} weight={600}>
              Measured With*
            </Typography17>
          </InputLabel>
          <MeasureComponent goalDetails={taskDetails} updateGoal={updateTask} />
        </StyledGrid>

        <StyledDivider bottomGutter={3} width="100%" topGutter={3} />

        <StyledGrid style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: '1.4rem' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'space-between',
            }}
          >
            <Heading>Owner(s)</Heading>
            <OwnerField taskDetails={taskDetails} updateTask={updateTask} />
          </div>
          <div style={{ width: '40px' }} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'space-between',
            }}
          >
            <Heading>Collaborator(s)</Heading>
            <CollaboratorField taskDetails={taskDetails} updateTask={updateTask} />
          </div>
        </StyledGrid>
      </StyledGrid>
      {/* {renderObjectiveSection()} */}

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Heading>Description</Heading>
        <CreateGoalDescriptionField defaultValue="" value={description} onKeyDown={onKeyDown} onUpdate={handleUpdate} />
      </div>
      <div
        style={{
          alignItems: 'center',
          marginTop: '1.5rem',
          display: 'flex',
        }}
      >
        <Heading>Visibility</Heading>
        <SelectWrapper>
          <VisibilityComponent
            defaultGoalVisibility={defaultGoalVisibility}
            goalDetails={taskDetails}
            updateGoal={updateTask}
          />
        </SelectWrapper>
      </div>
      {/* <StyledDivider variant="fullWidth" bottomGutter={2} topGutter={3} /> */}
      <div style={{ display: 'flex', marginTop: '1.5rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flex: '1',
            alignItems: 'center',
          }}
        >
          <Heading>Start on</Heading>
          <DueDateWrapper>
            <DueDateField
              clearable={false}
              dueDate={new Date(goalStartDateTS) ?? null}
              color={coloredStartDate.color}
              tooltipText={coloredStartDate.reason}
              isCrossed={coloredStartDate.isCrossed}
              detailedStatus={taskDetails.detailedStatus}
              updateDueDate={(date) => {
                updateTask({
                  ...taskDetails,
                  startDate: date,
                  coloredStartDate: {
                    ...coloredStartDate,
                    color: date ? theme.palette.primary.main : theme.palette.grey.dark,
                    timeStamp: date ? date.getTime() : null,
                  },
                  isDateEditedByUser: true,
                });
                setGoalStartDateTS(date);
              }}
            />
          </DueDateWrapper>
        </div>
        <div style={{ width: '40px' }} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flex: '1',
            alignItems: 'center',
          }}
        >
          <Heading>Due by</Heading>
          <DueDateWrapper>
            <DueDateField
              clearable={false}
              dueDate={new Date(goalDueDateTS) ?? null}
              color={coloredDueDate.color}
              tooltipText={coloredDueDate.reason}
              isCrossed={coloredDueDate.isCrossed}
              detailedStatus={taskDetails.detailedStatus}
              updateDueDate={(date) => {
                updateTask({
                  ...taskDetails,
                  dueDate: date,
                  coloredDueDate: {
                    ...coloredDueDate,
                    color: date ? theme.palette.primary.main : theme.palette.grey.dark,
                    timeStamp: date ? date.getTime() : null,
                  },
                  isDateEditedByUser: true,
                });
                setGoalDueDateTS(date);
              }}
              minDate={taskDetails?.coloredStartDate?.timeStamp}
            />
          </DueDateWrapper>
        </div>
      </div>
      {/* todo
        ORG_ID[orgId] &&
        (ORG_ID[orgId] === 'sharechat' ||
          ORG_ID[orgId] === 'mesh' ||
          ORG_ID[orgId] === 'meshDemo') &&
        renderTrackingSection() */}
    </Container>
  );
}
